import DayJsUtils from '@date-io/dayjs'
import { MantineProvider } from '@mantine/core'
import { StylesProvider } from '@material-ui/core'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import * as Sentry from '@sentry/react'
import { parse, stringify } from 'query-string'
import { createRoot } from 'react-dom/client'
import { BrowserRouter as Router } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { Theme, percentPalette } from '@percent/admin-dashboard/app/theme/Theme'
import { WithAxios } from '@percent/admin-dashboard/common/HOC/withAxios/WithAxios'
import { AuthContextController } from '@percent/admin-dashboard/context/auth'
import { env } from '@percent/admin-dashboard/env'
import { worker } from '@percent/admin-dashboard/mocks/browser'
import { LemonadeProvider, ErrorBoundaryFallback } from '@percent/lemonade'
import { App } from './app/app-entry'
import { logger } from './common/utility/logger'
import { ServiceContextContainer } from './containers/service/ServiceContextContainer'
import { ActivityTagsContextController } from './context/activityTags'
import { CountriesProvider } from './context/countries'
import { CurrenciesContextController } from './context/currencies'
import { FoundationsContextController } from './context/foundations'
import { PostHogProvider } from './app/providers/PostHogProvider/PostHogProvider'

import '@mantine/core/styles/global.css'
import './index.css'

if (env.NX_PUBLIC_USE_SENTRY_LOGGER === true) {
  Sentry.init({
    dsn: env.NX_PUBLIC_SENTRY_DSN,
    autoSessionTracking: true,
    environment: env.NX_PUBLIC_REACT_APP_ENV,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.33
  })
}

if (env.NODE_ENV === 'development') {
  worker.start()
}

const container = document.getElementById('root')

const root = createRoot(container!)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
})

root.render(
  <Sentry.ErrorBoundary fallback={<ErrorBoundaryFallback />} onError={(error: Error) => logger(error)}>
    <QueryClientProvider client={queryClient}>
      <Router>
        <MantineProvider>
          <LemonadeProvider customPalette={percentPalette}>
            <QueryParamProvider
              adapter={ReactRouter6Adapter}
              options={{
                searchStringToObject: parse,
                objectToSearchString: stringify
              }}
            >
              <StylesProvider injectFirst>
                <ServiceContextContainer>
                  <AuthContextController>
                    <PostHogProvider>
                      <FoundationsContextController>
                        <CurrenciesContextController>
                          <ActivityTagsContextController>
                            <CountriesProvider>
                              <Theme>
                                <MuiPickersUtilsProvider utils={DayJsUtils}>
                                  <WithAxios>
                                    <LemonadeProvider customPalette={percentPalette}>
                                      <App />
                                    </LemonadeProvider>
                                  </WithAxios>
                                </MuiPickersUtilsProvider>
                              </Theme>
                            </CountriesProvider>
                          </ActivityTagsContextController>
                        </CurrenciesContextController>
                      </FoundationsContextController>
                    </PostHogProvider>
                  </AuthContextController>
                </ServiceContextContainer>
              </StylesProvider>
            </QueryParamProvider>
          </LemonadeProvider>
        </MantineProvider>
      </Router>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
)
